import { Box, Container, Typography, Button } from '@mui/material';
import { ASSETS } from '../../../const/assets';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuthGoogle, useAuthApple } from '../../../hooks/useAuthencation';
import { signInWithApple } from '../../../util/appleSignIn';
import { useState } from 'react';

// Get Apple Client ID from environment variables
const APPLE_CLIENT_ID = import.meta.env.VITE_APP_APPLE_CLIENT_ID as string;
const APPLE_REDIRECT_URI = import.meta.env
  .VITE_APP_APPLE_REDIRECT_URI as string;

function MainLogin() {
  const navigate = useNavigate();
  const { mutateAsync: googleAuth } = useAuthGoogle();
  const { mutateAsync: appleAuth } = useAuthApple();
  const [isLoading, setIsLoading] = useState(false);

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      try {
        // Exchange code for tokens
        const tokenResponse = await fetch(
          'https://oauth2.googleapis.com/token',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              code: codeResponse.code,
              client_id: import.meta.env.VITE_APP_GOOGLE_CLIENT_ID,
              client_secret: import.meta.env.VITE_APP_GOOGLE_CLIENT_SECRET,
              redirect_uri: window.location.origin,
              grant_type: 'authorization_code',
            }),
          }
        );

        const tokens = await tokenResponse.json();

        // tokens.id_token contains the ID token needed for backend verification
        await googleAuth(tokens.id_token);
        navigate('/');
      } catch (error) {
        console.error('Google login failed:', error);
      }
    },
    onError: (errorResponse) => {
      console.error('Google Login Failed:', errorResponse);
    },
  });

  const handleGoogleButtonClick = () => {
    login();
  };

  const handleAppleButtonClick = async () => {
    try {
      setIsLoading(true);
      const response = await signInWithApple(
        APPLE_CLIENT_ID,
        APPLE_REDIRECT_URI
      );

      if (response.authorization && response.authorization.id_token) {
        // Get name from the response if available
        const firstName = response.user?.name?.firstName;
        const lastName = response.user?.name?.lastName;

        // Auth with backend
        await appleAuth({
          idToken: response.authorization.id_token,
          firstName,
          lastName,
        });

        navigate('/');
      }
    } catch (error) {
      console.error('Apple login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const TextComponent = ({ text }: { text: string }) => {
    return (
      <Typography color="white" sx={{ fontSize: '24px', fontWeight: 'bold' }}>
        {text}
      </Typography>
    );
  };

  const ButtonComponent = ({
    text,
    icon,
    click,
    disabled = false,
  }: {
    text: string;
    icon: string;
    click: () => void;
    disabled?: boolean;
  }) => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={click}
        disabled={disabled}
        sx={{
          width: '100%',
          height: '48px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 'medium',
          backgroundColor: 'white',
          textTransform: 'none',
          color: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:disabled': {
            backgroundColor: '#cccccc',
            color: '#666666',
          },
        }}
      >
        <img
          src={icon}
          alt="icon"
          width={24}
          height={24}
          className="mr-4 object-cover"
        />
        {text}
      </Button>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      position="relative"
      sx={{
        background: '#272727',
      }}
    >
      <img
        className="absolute top-0 left-0 w-full"
        src={ASSETS.bgLogin}
        alt="bgLogin"
      />
      <Container maxWidth="xs">
        <Box className="flex items-center gap-2 flex-col relative z-10">
          <img
            src={ASSETS.icLogo}
            alt="logo"
            width={80}
            height={80}
            className="mb-15"
          />
          <TextComponent text="Use premium" />
          <TextComponent text="AI Tools" />
          <TextComponent text="For free" />
          <Box className="flex items-center gap-4 flex-col mt-15 w-5/6">
            <ButtonComponent
              text="Continue with Google"
              icon={ASSETS.icGoogle}
              click={handleGoogleButtonClick}
            />
            <ButtonComponent
              text="Continue with Apple"
              icon={ASSETS.icApple}
              click={handleAppleButtonClick}
              disabled={isLoading}
            />
            <ButtonComponent
              text="Sign up with email"
              icon={ASSETS.icMail2}
              click={() => {
                navigate('/register');
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                navigate('/login');
              }}
              sx={{
                width: '100%',
                height: '48px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'medium',
                backgroundColor: '#333333',
                textTransform: 'none',
                color: 'white',
                border: '1px solid #B9BCC4',
              }}
            >
              Sign in with email
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default MainLogin;
